import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MosaicLogoFull } from '@/components/MosaicLogo';
import { createJingleContext, playJingle } from '@/utils/loginJingle';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [authUser, setAuthUser] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const user = await login(sessionId);
        setAuthUser(user);
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(true);
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    processAuth();
  }, [location, login, navigate]);

  const getDestination = (user) => {
    if (!user) return '/';
    if (user.role === 'super_admin') return '/superadmin';
    if (user.role === 'erma_reviewer') return '/erma-review';
    if (user.role === 'district_admin') return '/admin/district';
    if (user.role === 'school_admin') return '/admin/school';
    if (user.role === 'teacher') return '/teacher';
    if (user.role === 'student') return '/my-assessment';
    if (user.role === 'parent') return '/parent';
    return '/grades';
  };

  const handleContinue = () => {
    const ctx = createJingleContext();
    playJingle(ctx);
    setTimeout(() => navigate(getDestination(authUser)), 150);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-red-600">Authentication failed. Redirecting to login...</p>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center space-y-6" data-testid="welcome-screen">
        <MosaicLogoFull />
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome back, {authUser.name || authUser.email}
          </h2>
          <p className="text-sm text-gray-500 mt-1 capitalize">{authUser.role?.replace('_', ' ')}</p>
        </div>
        <Button
          onClick={handleContinue}
          className="bg-[#002FA7] hover:bg-[#0024a0] text-white px-8 py-3 text-base"
          data-testid="continue-to-mosaic"
        >
          Continue to MOSAIC <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default AuthCallback;
